import React, { useState } from 'react';
import { MessageSquareHeart, ChevronRight, Video } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  expired: { label: 'Fin de licence', tone: 'bg-amber-500/15 text-amber-300 border-amber-400/40' },
  expiring: { label: 'Derniers jours', tone: 'bg-sky-500/15 text-sky-300 border-sky-400/40' },
  admin: { label: 'Sur demande', tone: 'bg-purple-500/15 text-purple-300 border-purple-400/40' },
};

const FIELDS = [
  { id: 'appreciated', label: "Ce que j'ai apprécié" },
  { id: 'regrets', label: 'Ce que je regrette' },
  { id: 'improvements', label: "Les axes d'amélioration" },
  { id: 'overall', label: 'Ressenti global' },
];

/** Supervision formateur — REX de la cohorte (textes + vidéo), en lecture
 *  seule, pour enrichir les débriefs. Co-responsables : mêmes droits. */
export default function FormateurRexSection({ items = [] }) {
  const [open, setOpen] = useState(false);
  const [openVideo, setOpenVideo] = useState(null);

  return (
    <section className="bg-rose-900/15 border border-rose-500/40 rounded-lg overflow-hidden" data-testid="formateur-rex-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-rose-900/25 transition-colors"
        data-testid="formateur-rex-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageSquareHeart className="w-4 h-4 text-rose-300" />
          <span className="text-sm font-bold text-rose-100 uppercase tracking-wider">
            REX de la cohorte
          </span>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-bold font-mono ${items.length > 0 ? 'bg-rose-500/30 text-rose-100' : 'bg-slate-700 text-slate-400'}`}
            data-testid="formateur-rex-count"
          >
            {items.length}
          </span>
          <span className="text-[10px] text-rose-300/70 italic hidden sm:inline">
            Retours d'expérience des apprenants — textes et vidéos pour vos débriefs
          </span>
        </div>
        <ChevronRight className={`w-4 h-4 text-rose-300 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="p-4 border-t border-rose-500/30 space-y-3">
          {items.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4" data-testid="formateur-rex-empty">
              Aucun REX reçu pour le moment. Les apprenants sont invités à témoigner
              en fin de licence (ou sur demande de l'administrateur).
            </p>
          )}

          {items.map((rex) => {
            const src = SOURCE_LABELS[rex.source] || SOURCE_LABELS.expiring;
            return (
              <div
                key={rex.id}
                className="bg-ink-800 border border-ink-600 rounded-xl p-4"
                data-testid={`formateur-rex-card-${rex.id}`}
              >
                <div className="flex items-center gap-2.5 flex-wrap">
                  <div className="w-8 h-8 rounded-full bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
                    <span className="text-rose-300 font-bold text-xs">
                      {(rex.holder_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-slate-100 text-sm">{rex.holder_name || '—'}</p>
                    <p className="text-[10px] text-slate-500 font-mono">
                      {rex.license_key} · {new Date(rex.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${src.tone}`}>{src.label}</span>
                  {rex.video_token && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-money/10 text-money border border-money/40 font-bold flex items-center gap-1">
                      <Video className="w-3 h-3" /> Vidéo
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {FIELDS.map((f) => (
                    <div key={f.id} className="bg-background border border-ink-600 rounded-lg p-3">
                      <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">{f.label}</p>
                      <p className="text-xs text-slate-300 whitespace-pre-wrap">
                        {(rex[f.id] || '').trim() || <em className="text-slate-600">— non renseigné —</em>}
                      </p>
                    </div>
                  ))}
                </div>

                {rex.video_token && (
                  <div className="mt-3">
                    {openVideo === rex.id ? (
                      <video
                        controls
                        autoPlay
                        playsInline
                        src={`${BACKEND}/api/feedback/rex/video/${rex.video_token}`}
                        className="w-full max-h-80 rounded-lg bg-black"
                        data-testid={`formateur-rex-video-player-${rex.id}`}
                      />
                    ) : (
                      <button
                        onClick={() => setOpenVideo(rex.id)}
                        className="flex items-center gap-2 px-4 py-2 border border-money/40 text-money rounded-lg text-xs font-bold hover:bg-money/10"
                        data-testid={`formateur-rex-play-video-${rex.id}`}
                      >
                        <Video className="w-4 h-4" /> Lire le témoignage vidéo
                        {rex.video_size ? <span className="text-slate-500 font-normal">({Math.round(rex.video_size / 1024 / 1024 * 10) / 10} Mo)</span> : null}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
